import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  CheckCircle2, Circle, ArrowLeft, ArrowRight, ChevronLeft, Loader2, Trophy,
  ListChecks, BookOpenCheck, Lock,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INDIGO = '#4338CA';

export default function SOPWalkthroughPage() {
  const { assignmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [finalSaving, setFinalSaving] = useState(false);
  const [celebrated, setCelebrated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/sops/assignments/${assignmentId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load assignment');
    }
    setLoading(false);
  }, [assignmentId]);

  useEffect(() => { load(); }, [load]);

  if (!user) return null;
  if (loading) return <Shell><Center><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></Center></Shell>;
  if (err) return <Shell><Center><p className="text-sm text-red-500">{err}</p><Link to="/sops" className="text-xs mt-3" style={{ color: INDIGO }}>Back to SOPs</Link></Center></Shell>;
  if (!data) return null;

  const { assignment, sop, steps, completed_step_indexes } = data;
  const completedSet = new Set(completed_step_indexes);
  const currentStep = steps[active];
  const isLast = active === steps.length - 1;
  const allDone = completedSet.size === steps.length;
  const isComplete = assignment.status === 'completed';

  const markStep = async () => {
    if (completedSet.has(active) || isComplete) return;
    setSaving(true);
    try {
      await axios.post(`${API}/sops/assignments/${assignmentId}/step/${active}/complete`, {}, { withCredentials: true });
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const next = () => { if (active < steps.length - 1) setActive(active + 1); };
  const prev = () => { if (active > 0) setActive(active - 1); };

  const finalComplete = async () => {
    if (!allDone) return;
    setFinalSaving(true);
    try {
      await axios.post(`${API}/sops/assignments/${assignmentId}/complete`, {}, { withCredentials: true });
      setCelebrated(true);
      setTimeout(() => navigate('/sops'), 3500);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to complete');
      setFinalSaving(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/sops')} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="wt-back">
          <ChevronLeft className="w-3.5 h-3.5" /> My tasks
        </button>

        {/* Header strip */}
        <div className="rounded-2xl border p-5 mb-5" style={{ borderColor: `${INDIGO}33`, background: `linear-gradient(135deg, ${INDIGO}0A, transparent 60%)` }} data-testid="wt-header">
          <div className="flex items-center gap-2 mb-2">
            <BookOpenCheck className="w-4 h-4" style={{ color: INDIGO }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: INDIGO }}>SOP Walkthrough · {sop.category}</span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{sop.title}</h1>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${INDIGO}15` }}>
              <div className="h-full transition-all" style={{ width: `${(completedSet.size / steps.length) * 100}%`, backgroundColor: INDIGO }} />
            </div>
            <span className="text-xs font-semibold" style={{ color: INDIGO }} data-testid="wt-progress-label">{completedSet.size} / {steps.length} steps</span>
          </div>
          {isComplete && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: '#16A34A15', color: '#16A34A' }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Completed {assignment.completed_at?.slice(0, 10)}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
          {/* Sidebar */}
          <nav className="rounded-2xl border border-border bg-card p-3 self-start" data-testid="wt-sidebar">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2 pt-1 pb-2 flex items-center gap-1.5"><ListChecks className="w-3 h-3" /> Steps</p>
            <ul className="space-y-1">
              {steps.map((s, i) => {
                const done = completedSet.has(i);
                const isActive = i === active;
                return (
                  <li key={i}>
                    <button
                      onClick={() => setActive(i)}
                      data-testid={`wt-sidebar-step-${i}`}
                      className={`w-full text-left px-2.5 py-2 rounded-lg flex items-start gap-2 text-xs transition-colors ${isActive ? 'font-semibold' : 'hover:bg-accent'}`}
                      style={isActive ? { backgroundColor: `${INDIGO}12`, color: INDIGO } : undefined}
                    >
                      <span className="shrink-0 mt-0.5">{done ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#16A34A' }} /> : <Circle className="w-3.5 h-3.5 text-muted-foreground" />}</span>
                      <span className="flex-1">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Step {i + 1}</span>
                        <span className="block leading-snug">{s.title}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Main body */}
          <article className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="wt-body">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${INDIGO}15`, color: INDIGO }}>
                Step {active + 1} of {steps.length}
              </span>
              {completedSet.has(active) && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#16A34A15', color: '#16A34A' }}>
                  <CheckCircle2 className="w-3 h-3" /> Completed
                </span>
              )}
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-foreground mb-4">{currentStep.title}</h2>
            <div className="prose prose-sm dark:prose-invert max-w-none sop-walkthrough-md" data-testid="wt-step-body">
              <ReactMarkdown>{currentStep.body || '*No content for this step.*'}</ReactMarkdown>
            </div>

            {/* Step nav buttons */}
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-border flex-wrap gap-3">
              <button onClick={prev} disabled={active === 0} data-testid="wt-prev-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border border-border disabled:opacity-40">
                <ArrowLeft className="w-3.5 h-3.5" /> Previous
              </button>
              <div className="flex items-center gap-2">
                {!isComplete && !completedSet.has(active) && (
                  <button onClick={markStep} disabled={saving} data-testid="wt-mark-step"
                    className="inline-flex items-center gap-1.5 text-sm font-bold px-5 py-2 rounded-xl text-white disabled:opacity-60"
                    style={{ backgroundColor: INDIGO }}>
                    <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Mark step complete'}
                  </button>
                )}
                {!isLast && (
                  <button onClick={next} data-testid="wt-next-btn"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border-2 transition-colors"
                    style={{ borderColor: INDIGO, color: INDIGO }}>
                    Next step <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Final sign-off — only shown on last step */}
            {isLast && !isComplete && (
              <div className="mt-6 p-5 rounded-2xl border-2" style={{ borderColor: allDone ? '#16A34A' : 'hsl(var(--border))', backgroundColor: allDone ? '#16A34A08' : 'transparent' }} data-testid="wt-final-panel">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: allDone ? '#16A34A' : '#94A3B8', color: 'white' }}>
                    {allDone ? <Trophy className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Outfit'] font-bold text-foreground">{allDone ? 'Ready to finish!' : 'Finish the remaining steps to complete'}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {allDone ? 'Click below to sign off — your name goes on the governance record.' : `${steps.length - completedSet.size} step(s) still need "Mark step complete". Use the sidebar to jump to them.`}
                    </p>
                    <button onClick={finalComplete} disabled={!allDone || finalSaving} data-testid="wt-final-complete"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl text-white disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#16A34A' }}>
                      <Trophy className="w-4 h-4" /> {finalSaving ? 'Recording…' : 'I have completed this SOP'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </article>
        </div>
      </div>

      {celebrated && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="wt-celebration">
          <div className="bg-card border border-border rounded-2xl p-8 max-w-sm text-center">
            <Trophy className="w-14 h-14 mx-auto mb-3" style={{ color: '#16A34A' }} />
            <h3 className="font-['Outfit'] text-2xl font-bold text-foreground">Well done!</h3>
            <p className="text-sm text-muted-foreground mt-1">You've completed {sop.title}. It's now on your compliance record.</p>
            <p className="text-[11px] text-muted-foreground mt-4">Returning to My Tasks…</p>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-background"><Header />{children}</div>;
}
function Center({ children }) {
  return <div className="max-w-md mx-auto flex flex-col items-center justify-center py-24">{children}</div>;
}
